/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import {DiscoveryCategoryBottomSheet} from '@app/components/bottomsheets/DiscoveryCategoryBottomSheet';
import {DiscoveryGuildCard} from '@app/components/discovery/DiscoveryGuildCard';
import styles from '@app/components/discovery/DiscoveryPage.module.css';
import {Input} from '@app/components/form/Input';
import {Scroller} from '@app/components/uikit/Scroller';
import {Spinner} from '@app/components/uikit/Spinner';
import foodPatternUrl from '@app/images/i-like-food.svg';
import DiscoveryStore from '@app/stores/DiscoveryStore';
import MobileLayoutStore from '@app/stores/MobileLayoutStore';
import {useLingui} from '@lingui/react/macro';
import {FunnelIcon, MagnifyingGlassIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useRef, useState} from 'react';

const SCROLL_THRESHOLD = 0.7;
const SEARCH_DEBOUNCE_MS = 300;

export const DiscoveryPage = observer(function DiscoveryPage() {
	const {t} = useLingui();
	const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const isMobile = MobileLayoutStore.enabled;
	const [categorySheetOpen, setCategorySheetOpen] = useState(false);

	const handleSearchChange = useCallback((value: string) => {
		if (searchTimerRef.current) {
			clearTimeout(searchTimerRef.current);
		}
		searchTimerRef.current = setTimeout(() => {
			void DiscoveryStore.search({query: value, offset: 0});
		}, SEARCH_DEBOUNCE_MS);
	}, []);

	const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
		const target = event.currentTarget;
		const scrollPosition = target.scrollTop + target.clientHeight;
		const scrollHeight = target.scrollHeight;

		if (
			scrollPosition / scrollHeight >= SCROLL_THRESHOLD &&
			!DiscoveryStore.loading &&
			DiscoveryStore.guilds.length < DiscoveryStore.total
		) {
			void DiscoveryStore.search({offset: DiscoveryStore.guilds.length});
		}
	}, []);

	const hasMore = DiscoveryStore.guilds.length < DiscoveryStore.total;

	return (
		<div className={styles.container}>
			<Scroller showTrack={false} onScroll={handleScroll}>
				<div className={styles.hero}>
					<div className={styles.heroBackground} aria-hidden>
						<div className={styles.heroPattern} style={{backgroundImage: `url(${foodPatternUrl})`}} />
					</div>
					<div className={styles.heroContent}>
						<div className={styles.heroControls}>
							<Input
								className={styles.searchInput}
								placeholder={t`Search communities...`}
								defaultValue={DiscoveryStore.query}
								onChange={(e) => handleSearchChange(e.target.value)}
								leftIcon={<MagnifyingGlassIcon size={16} weight="bold" />}
							/>
							{isMobile && (
								<button type="button" className={styles.filterButton} onClick={() => setCategorySheetOpen(true)}>
									<FunnelIcon size={20} weight="bold" />
								</button>
							)}
						</div>
					</div>
				</div>

				<div className={styles.content}>
					{DiscoveryStore.loading && DiscoveryStore.guilds.length === 0 ? (
						<div className={styles.loadingState}>
							<Spinner />
						</div>
					) : DiscoveryStore.guilds.length > 0 ? (
						<>
							<div className={styles.grid}>
								{DiscoveryStore.guilds.map((guild) => (
									<DiscoveryGuildCard key={guild.id} guild={guild} />
								))}
							</div>
							{hasMore && DiscoveryStore.loading && (
								<div className={styles.loadingMore}>
									<Spinner />
								</div>
							)}
						</>
					) : (
						<div className={styles.emptyState}>
							<span className={styles.emptyStateText}>{t`No communities found.`}</span>
						</div>
					)}
				</div>
			</Scroller>

			{isMobile && (
				<DiscoveryCategoryBottomSheet isOpen={categorySheetOpen} onClose={() => setCategorySheetOpen(false)} />
			)}
		</div>
	);
});
